import { create } from 'zustand';

export interface AuthUser {
  id: string;
  email: string;
  tenantId: string;
  roles: string[];
  mfaEnabled: boolean;
  /**
   * The tenant's enabled module keys, from `platform.module_activations`.
   * An `agency` key switches the whole shell to the agency product.
   *
   * Optional because a session persisted by an older build has no such key;
   * absent reads as "no modules", which falls back to the HR nav rather than
   * to a blank sidebar.
   */
  modules?: string[] | undefined;
}

const USER_KEY = 'hrms_user';
const TENANT_KEY = 'hrms_tenant_slug';

export function persistUser(user: AuthUser, tenantSlug: string): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(TENANT_KEY, tenantSlug);
}

export function restoreUser(): { user: AuthUser; tenantSlug: string } | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    const slug = localStorage.getItem(TENANT_KEY);
    if (!raw || !slug) return null;
    return { user: JSON.parse(raw) as AuthUser, tenantSlug: slug };
  } catch {
    return null;
  }
}

export function clearPersistedUser(): void {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TENANT_KEY);
}

export function getTenantSlug(): string | null {
  return localStorage.getItem(TENANT_KEY);
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  tenantSlug: string | null;
  isAuthenticated: boolean;
  setAuth(user: AuthUser, accessToken: string, tenantSlug: string): void;
  clearAuth(): void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  accessToken: null,
  tenantSlug: null,
  isAuthenticated: false,

  setAuth(user, accessToken, tenantSlug) {
    set({ user, accessToken, tenantSlug, isAuthenticated: true });
  },

  clearAuth() {
    set({ user: null, accessToken: null, tenantSlug: null, isAuthenticated: false });
  },
}));
